// src/features/patient/care-treatment/components/ReviewTab.tsx

/**
 * Purpose : The To review tab, calmer: each note we could not
 * place shows its first 3 lines as written beside the suggested visit's
 * details (date, type, clinician, facility), with Confirm visit / Keep
 * unlinked; "Confirm all suggestions from this file" confirms a whole file's
 * suggestions at once (each one on its own; failures are reported). A note
 * whose day has two or more visits is a pick among that day's visits only.
 * Nothing else is here (2026-10-29): notes with no visit are in Calls,
 * messages & letters, and pages that aren't notes aren't listed.
 *
 * Contents :
 *
 * * function(groupByFile) : the notes per uploaded file
 * * function(ReviewTab) : the tab
 * * function(ReviewItem) : one note and its suggested visit
 * * function(UnlinkedItem) : one note and the picker of its day's visits
 * * function(NotePreview) : a note's type, date, author and first 3 lines
 */

import { useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { Button } from "@/shared/components/button";
import { EmptyState } from "@/shared/components/EmptyState";
import { Select } from "@/shared/components/select";
import { cn } from "@/shared/lib/utils";
import { toastError, toastInfo } from "@/shared/lib/toast";
import { useAnswerNoteLink, useConfirmNoteLinks, useLinkNoteToVisit } from "@/portals/patient/api/patientQueries";
import type { Encounter, Note } from "@/features/patient/care-treatment/api/careTimelineTypes";
import {
  formatDateOnly, noteTypeLabel, notesToReview, pageRangeText,
} from "@/features/patient/care-treatment/utils/careTimeline";
import { recordTypeLabel, visitKind } from "@/features/patient/care-treatment/utils/careNaming";
import { noteLines } from "@/features/patient/care-treatment/utils/noteText";
import { ClampedText, KindTile } from "./careParts";
import { LoadOlderButton, usePaged } from "./LoadOlder";
import {
  brandButtonClass,
  cardClass,
  factBoxClass,
  mutedIconClass,
  outlineButtonClass,
  subTextClass,
  titleClass,
} from "./careStyles";

// function(groupByFile)
/** Notes per file (their first source), in the order they first appear. */
export function groupByFile(notes: Note[]): { file: string; notes: Note[] }[] {
  const groups = new Map<string, Note[]>();
  for (const note of notes) {
    const file = note.sources[0]?.label ?? "";
    groups.set(file, [...(groups.get(file) ?? []), note]);
  }
  return [...groups.entries()].map(([file, list]) => ({ file, notes: list }));
}

// function(ReviewTab)
export function ReviewTab({
  notes, encounterById, onOpenNote, actingAsProfileId, total, hasOlder = false, loadingOlder = false, onLoadOlder,
}: {
  notes: Note[];
  encounterById: Map<string, Encounter>;
  onOpenNote: (note: Note) => void;
  actingAsProfileId?: string;
  /** The server's count of notes to review (over every page of notes). */
  total?: number;
  /** Older pages of notes exist on the server; onLoadOlder fetches the next. */
  hasOlder?: boolean;
  loadingOlder?: boolean;
  onLoadOlder?: () => void;
}) {
  // Only real questions: a visit on the note's day to confirm or pick. Notes
  // with no visit live in Calls, messages & letters; pages that aren't notes
  // aren't listed (2026-10-29).
  const allItems = notesToReview(notes);
  // 10 at a time; "Load older" only when there are more.
  const pagedItems = usePaged(allItems);
  const items = pagedItems.shown;
  // Every loaded one shown, and the server counts more: they're on an older page of notes.
  const moreOnServer = Boolean(onLoadOlder) && hasOlder && !pagedItems.hasMore && (total ?? 0) > allItems.length;
  // Every visit and call / message the patient can pick for a note with none.
  const visits = useMemo(() => [...encounterById.values()], [encounterById]);
  const answerLink = useAnswerNoteLink(actingAsProfileId);
  const confirmAll = useConfirmNoteLinks(actingAsProfileId);
  const linkNote = useLinkNoteToVisit(actingAsProfileId);
  const busy = answerLink.isPending || confirmAll.isPending || linkNote.isPending;
  const answer = (noteId: string, confirm: boolean) =>
    answerLink.mutate({ noteId, confirm }, { onError: () => toastError("Couldn't save your answer. Please try again.") });
  const link = (noteId: string, encounterId: string) =>
    linkNote.mutate({ noteId, encounterId }, { onError: () => toastError("Couldn't save your answer. Please try again.") });
  const confirmFile = (fileNotes: Note[]) =>
    confirmAll.mutate(fileNotes.map((n) => n.id), {
      onSuccess: (results) => {
        const failed = results.filter((r) => !r.ok).length;
        if (failed > 0) toastError(`${failed} of ${results.length} couldn't be confirmed. They are still here to review.`);
        else toastInfo(`${results.length} confirmed.`);
      },
      onError: () => toastError("Couldn't save your answers. Please try again."),
    });

  return (
    <div className="grid gap-4">
      {/* Only when there is something to answer; otherwise "Nothing to review" says it. */}
      {items.length > 0 && (
        <p className={cn("!mb-0 px-1", subTextClass)}>
          We found these notes but couldn’t confirm which visit they belong to. Confirming keeps your record accurate.
        </p>
      )}
      {items.length === 0 && (
        <EmptyState
          icon={<Icon icon="lucide:list-checks" className={mutedIconClass} />}
          title="Nothing to review"
          description="When a note could belong to more than one visit on its day, it will appear here for you to check"
        />
      )}

      {items.length > 0 && (
        groupByFile(items).map(({ file, notes: fileNotes }) => (
          <section key={file || "no-file"} aria-label={file || "Notes to review"} className="grid gap-2">
            {(file || fileNotes.length > 1) && (
              <div className="flex flex-wrap items-center justify-between gap-3 px-1">
                {file ? <h2 className={cn("!mb-0 text-sm", titleClass)}>{file}</h2> : <span />}
                {fileNotes.length > 1 && (
                  <Button onClick={() => confirmFile(fileNotes)} disabled={busy} className={brandButtonClass}>
                    Confirm all suggestions from this file
                  </Button>
                )}
              </div>
            )}
            {fileNotes.map((note) => {
              const sameDay = visitsOnNoteDay(note, visits);
              // Two or more visits that day: a real choice, among those visits only.
              return sameDay.length >= 2 ? (
                <UnlinkedItem
                  key={note.id}
                  note={note}
                  visits={sameDay}
                  busy={busy}
                  onOpen={() => onOpenNote(note)}
                  onLink={(encounterId) => link(note.id, encounterId)}
                  onKeepUnlinked={() => answer(note.id, false)}
                />
              ) : (
                <ReviewItem
                  key={note.id}
                  note={note}
                  visit={note.suggestedEncounterId ? encounterById.get(note.suggestedEncounterId) : undefined}
                  busy={busy}
                  onOpen={() => onOpenNote(note)}
                  onAnswer={(confirm) => answer(note.id, confirm)}
                />
              );
            })}
          </section>
        ))
      )}
      <LoadOlderButton show={pagedItems.hasMore} onClick={pagedItems.more} />

      <LoadOlderButton show={moreOnServer} onClick={() => onLoadOlder?.()} busy={loadingOlder} />

    </div>
  );
}

// function(ReviewItem)
/** The calls-tab row pattern: type and date, author, the first 3 lines; the suggested visit beside it. */
function ReviewItem({ note, visit, busy, onOpen, onAnswer }: {
  note: Note;
  visit: Encounter | undefined;
  busy: boolean;
  onOpen: () => void;
  onAnswer: (confirm: boolean) => void;
}) {
  const visitFacts = visit
    ? [
        formatDateOnly(visit.start),
        recordTypeLabel(visit),
        visit.practitioner,
        [visit.facility, visit.department].filter(Boolean).join(" · "),
      ].filter(Boolean)
    : [];

  return (
    <article aria-label={noteTypeLabel(note)} className={cn(cardClass, "!p-4")}>
      <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_280px]">
        <NotePreview note={note} onOpen={onOpen} />
        {visitFacts.length > 0 && (
          <div className={cn(factBoxClass, "!p-3")}>
            <p className="!mb-1 text-[11px] font-bold uppercase tracking-wide text-[#9CA3AF]">Suggested visit</p>
            <div className="flex items-start gap-2">
              {visit && <KindTile kind={visitKind(visit)} size="sm" />}
              <div className="min-w-0 text-sm text-[#0A2947]">
                {visitFacts.map((fact, i) => (
                  <p key={i} className={cn("!mb-0 break-words", i === 0 && "font-bold")}>{fact}</p>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button onClick={() => onAnswer(true)} disabled={busy || !visit} className={brandButtonClass}>Confirm visit</Button>
        <Button variant="outline" onClick={() => onAnswer(false)} disabled={busy} className={outlineButtonClass}>Keep unlinked</Button>
      </div>
    </article>
  );
}

/** "Mar 4, 2024 · Office visit · Robert Kim, MD": a visit in the picker. */
const visitLabel = (visit: Encounter) =>
  [formatDateOnly(visit.start), recordTypeLabel(visit), visit.practitioner].filter(Boolean).join(" · ");

const dayNumber = (iso: string) => Date.parse(`${iso.slice(0, 10)}T00:00:00Z`) || 0;

/** The visits that span the note's own day ("" dates never match). */
export function visitsOnNoteDay(note: Note, visits: Encounter[]): Encounter[] {
  const day = note.date?.slice(0, 10);
  if (!day) return [];
  return visits.filter((v) => {
    const start = v.start?.slice(0, 10);
    const end = (v.end || v.start)?.slice(0, 10);
    return Boolean(start) && start <= day && day <= (end && end >= start ? end : start);
  });
}

// function(UnlinkedItem)
/** A note whose day has several visits: the patient picks which one (only that day's) or keeps it unlinked. */
function UnlinkedItem({ note, visits, busy, onOpen, onLink, onKeepUnlinked }: {
  note: Note;
  visits: Encounter[];
  busy: boolean;
  onOpen: () => void;
  onLink: (encounterId: string) => void;
  onKeepUnlinked: () => void;
}) {
  const [picked, setPicked] = useState<string | undefined>(undefined);
  const options = useMemo(() => {
    const day = dayNumber(note.date);
    return [...visits]
      .sort((a, b) => Math.abs(dayNumber(a.start) - day) - Math.abs(dayNumber(b.start) - day))
      .map((visit) => ({ value: visit.id, label: visitLabel(visit) }));
  }, [visits, note.date]);

  return (
    <article aria-label={noteTypeLabel(note)} className={cn(cardClass, "!p-4")}>
      <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_280px]">
        <NotePreview note={note} onOpen={onOpen} />
        <div className={cn(factBoxClass, "!p-3")}>
          <label htmlFor={`pick-visit-${note.id}`} className="!mb-1 block text-[11px] font-bold uppercase tracking-wide text-[#9CA3AF]">
            Which visit is it from?
          </label>
          <Select
            id={`pick-visit-${note.id}`}
            aria-label="Choose the visit"
            placeholder="Choose the visit"
            showSearch
            optionFilterProp="label"
            value={picked}
            onChange={(value) => setPicked(value as string)}
            options={options}
            disabled={options.length === 0}
          />
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button onClick={() => picked && onLink(picked)} disabled={busy || !picked} className={brandButtonClass}>Link to this visit</Button>
        <Button variant="outline" onClick={onKeepUnlinked} disabled={busy} className={outlineButtonClass}>Keep unlinked</Button>
      </div>
    </article>
  );
}

// function(NotePreview)
/** A note's type and date (opens it), author and pages, and its first 3 lines. */
function NotePreview({ note, onOpen }: { note: Note; onOpen: () => void }) {
  const type = noteTypeLabel(note);
  const pages = note.sources[0] ? pageRangeText(note.sources[0]) : "";
  const meta = [note.author, pages].filter(Boolean).join(" · ");
  const text = noteLines(note).join("\n");
  return (
    <div className="flex min-w-0 items-start gap-3">
      <KindTile kind="chart_note" size="sm" />
      <div className="min-w-0 flex-1">
        <button type="button" onClick={onOpen} className={cn("text-left text-sm hover:underline", titleClass)}>
          {[type, formatDateOnly(note.date)].filter(Boolean).join(" · ")}
        </button>
        {meta && <p className="!mb-0 truncate text-xs text-[#6B7280]">{meta}</p>}
        {text && <div className="mt-2"><ClampedText text={text} lines={3} /></div>}
      </div>
    </div>
  );
}

